import './mailCodePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import toString from 'lodash/toString';
import uniqueId from 'lodash/uniqueId';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { CircularProgressFullScreen } from '../../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { LanderImage } from '../../../components/LanderImage/LanderImage';
import useApiStore from '../../../store/apiStore/useApiStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { mailCode: parameterMailCode } = useParameterStore();
  const {
    computed: { brand },
  } = useLeadChannelStore();
  const {
    computed: { shouldFetchMailCodeDataOnLoad },
    fetchMailCodeDataFailedMessage,
    isMailCodeNotFoundError,
    isMailCodeInvalidSourceError,
    mailCode,
    fetchMailCodeData,
  } = useMailCodeStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      mailCodeValue: shouldFetchMailCodeDataOnLoad
        ? parameterMailCode
        : mailCode,
    },
    delayError: 200,
  });

  const mailCodeValidation = {
    required: t('validation.mailCode.required'),
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { mailCodeValue } = values;

    useMailCodeStore.setState({
      mailCode: mailCodeValue,
    });

    await fetchMailCodeData(mailCodeValue);

    const {
      computed: { offerStatusRoute },
      fetchMailCodeDataSuccess,
    } = useMailCodeStore.getState();

    if (fetchMailCodeDataSuccess) {
      navigate(offerStatusRoute);
    }
  };

  useEffect((): void => {
    if (isMailCodeInvalidSourceError) {
      navigate('/sign-up');
    }
  }, [isMailCodeInvalidSourceError]);

  useEffect((): void => {
    if (shouldFetchMailCodeDataOnLoad) {
      handleSubmit(formSubmit)();
    }
  }, [shouldFetchMailCodeDataOnLoad]);

  // display a full-screen loading spinner while attempting to fetch mail code data on load.
  if (shouldFetchMailCodeDataOnLoad) {
    return <CircularProgressFullScreen />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="page mail-code-page">
        <Header showLogo splitLayout />

        <main>
          <LanderImage
            alt={t('imageAlts.vehicleSUVTopView')}
            defaultImage={vehicleSUVTopView}
          />
          <form
            data-testid={locator.pages.mailCode.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.mailCode.form.h1}>
                <Trans
                  components={[<u key={uniqueId('u-')} />]}
                  i18nKey={'MailCodePage.title'}
                  values={{
                    brand,
                  }}
                />
              </h1>
              <h2 data-testid={locator.pages.mailCode.form.h2}>
                <Trans
                  components={[<u key={uniqueId('u-')} />]}
                  i18nKey={'MailCodePage.subtitle'}
                />
              </h2>

              {!isMailCodeNotFoundError &&
                !isMailCodeInvalidSourceError &&
                fetchMailCodeDataFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.mailCode.alert.error}
                      severity="error"
                    >
                      {fetchMailCodeDataFailedMessage}
                    </Alert>
                  </div>
                )}

              <div style={{ marginBottom: '3rem' }}>
                <Input
                  {...register('mailCodeValue', mailCodeValidation)}
                  data-testid={locator.pages.mailCode.form.mailCodeInput}
                  error={
                    errors.mailCodeValue?.message ||
                    (isMailCodeNotFoundError
                      ? toString(fetchMailCodeDataFailedMessage)
                      : '')
                  }
                  errorTestId={locator.pages.mailCode.form.mailCodeInputError}
                  label={t('MailCodePage.savingsId')}
                />
              </div>

              <Button
                data-testid={locator.pages.mailCode.form.submitButton}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('MailCodePage.continue')}
              </Button>

              <p className="form-sign-up">
                <Link
                  data-testid={locator.pages.mailCode.form.signupLink}
                  to="/sign-up"
                >
                  {t('MailCodePage.missingSavingsId')}
                </Link>
              </p>
            </div>
            <footer data-testid={locator.pages.mailCode.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'MailCodePage';
